import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getOrder } from '../../services/orderService'
import type { OrderActions } from '../../hooks/useOrderActions'
import type { DealerOrderEvent, DealerOrderItem } from '../../types'
import { formatDateTime, formatVehicle } from '../../utils/format'
import { CopiedHint, DeliveryBadge, IssuePills, ReportLinkActions, SendAction } from './OrderParts'
import PhoneEditor from './PhoneEditor'
import { CloseIcon, UndoIcon } from './icons'

interface Props {
  item: DealerOrderItem
  actions: OrderActions
  onClose: () => void
}

// задержка перечитывания истории: событие копирования отправляется fire-and-forget
const EVENTS_REFRESH_DELAY_MS = 800

// Боковая панель заказа: те же действия, что в строке, плюс доставка и история событий
export default function OrderDetailPanel({ item, actions, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const closeRef = useRef<HTMLButtonElement>(null)
  const [events, setEvents] = useState<DealerOrderEvent[] | null>(null)
  const [eventsError, setEventsError] = useState(false)

  useEffect(() => {
    closeRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // перечитываем историю при открытии и после действий с заказом
  const changeKey = `${item.id}|${item.deliveryStatus}|${item.sentAt}|${item.linkCopiedAt}|${item.phoneCopiedAt}|${item.clientPhone}`
  const loadedIdRef = useRef<string | null>(null)
  useEffect(() => {
    let cancelled = false
    const isFirstLoad = loadedIdRef.current !== item.id
    if (isFirstLoad) setEvents(null)
    const timer = window.setTimeout(() => {
      getOrder(item.id)
        .then((detail) => {
          if (cancelled) return
          loadedIdRef.current = item.id
          setEvents(detail.events ?? [])
          setEventsError(false)
        })
        .catch((err) => {
          if (cancelled) return
          console.error(`[ERROR] Failed to load order detail — id: ${item.id}`, err)
          setEventsError(true)
        })
    }, isFirstLoad ? 0 : EVENTS_REFRESH_DELAY_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [changeKey, item.id])

  const vehicle = formatVehicle(item.carYear, item.carModel)
  const sortedEvents = events ? [...events].sort((a, b) => b.createdAt.localeCompare(a.createdAt)) : null
  const canUnmark = item.deliveryStatus === 'SENT' && !actions.isJustSent(item.id)

  return (
    <div className="dealer-panel-overlay" onClick={onClose}>
      <aside
        className="dealer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dealer-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dealer-panel__header">
          <div>
            <h2 id="dealer-panel-title" className="dealer-panel__title">
              {t('dealer.orders.order_number', { number: item.orderNumber })}
            </h2>
            <p className="dealer-panel__subtitle">{formatDateTime(item.createdAt, lang)}</p>
          </div>
          <button ref={closeRef} type="button" className="dealer-icon-btn" onClick={onClose} aria-label={t('dealer.common.close')}>
            <CloseIcon />
          </button>
        </header>

        <div className="dealer-panel__body">
          <section className="dealer-panel__status">
            <DeliveryBadge item={item} />
            <CopiedHint item={item} />
          </section>

          {(item.deliveryStatus === 'NOT_SENT' || actions.isJustSent(item.id)) && (
            <div className="dealer-panel__primary">
              <SendAction item={item} actions={actions} block />
            </div>
          )}

          <dl className="dealer-panel__list">
            <dt>{t('dealer.orders.col_customer')}</dt>
            <dd><PhoneEditor item={item} actions={actions} /></dd>

            <dt>{t('dealer.orders.col_report')}</dt>
            <dd><ReportLinkActions item={item} actions={actions} /></dd>

            <dt>{t('dealer.orders.col_vehicle')}</dt>
            <dd>
              {vehicle || '—'}
              {item.vin && <span className="dealer-mono dealer-panel__vin">{item.vin}</span>}
            </dd>

            <dt>{t('dealer.orders.col_mechanic')}</dt>
            <dd>{item.mechanicName || '—'}</dd>

            <dt>{t('dealer.orders.col_issues')}</dt>
            <dd>{item.processingStatus === 'READY' ? <IssuePills item={item} /> : '—'}</dd>
          </dl>

          <h3 className="dealer-panel__section-title">{t('dealer.orders.section_delivery')}</h3>
          <dl className="dealer-panel__list">
            <dt>{t('dealer.orders.field_sent')}</dt>
            <dd>
              {item.sentAt
                ? item.sentByName
                  ? t('dealer.orders.sent_by', { date: formatDateTime(item.sentAt, lang), name: item.sentByName })
                  : formatDateTime(item.sentAt, lang)
                : '—'}
            </dd>
            <dt>{t('dealer.orders.field_first_viewed')}</dt>
            <dd>{item.firstViewedAt ? formatDateTime(item.firstViewedAt, lang) : '—'}</dd>
            <dt>{t('dealer.orders.field_last_viewed')}</dt>
            <dd>{item.lastViewedAt ? formatDateTime(item.lastViewedAt, lang) : '—'}</dd>
            <dt>{t('dealer.orders.field_view_count')}</dt>
            <dd>{item.viewCount ?? 0}</dd>
          </dl>

          {canUnmark && (
            <button type="button" className="dealer-text-btn" disabled={actions.isBusy(item.id)} onClick={() => actions.undoSent(item)}>
              <UndoIcon /> {t('dealer.orders.unmark_sent')}
            </button>
          )}

          <h3 className="dealer-panel__section-title">{t('dealer.orders.section_history')}</h3>
          {eventsError && !sortedEvents ? (
            <p className="dealer-muted">{t('dealer.orders.history_failed')}</p>
          ) : !sortedEvents ? (
            <div className="dealer-spinner dealer-spinner--sm" />
          ) : sortedEvents.length === 0 ? (
            <p className="dealer-muted">{t('dealer.orders.history_empty')}</p>
          ) : (
            <ol className="dealer-timeline">
              {sortedEvents.map((ev, i) => (
                <li key={`${ev.type}-${ev.createdAt}-${i}`} className={`dealer-timeline__item dealer-timeline__item--${ev.type.toLowerCase()}`}>
                  <span className="dealer-timeline__dot" aria-hidden="true" />
                  <span className="dealer-timeline__text">
                    {t(`dealer.orders.event_${ev.type.toLowerCase()}`)}
                    {ev.userName && <span className="dealer-muted"> · {ev.userName}</span>}
                  </span>
                  <span className="dealer-timeline__time">{formatDateTime(ev.createdAt, lang)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </aside>
    </div>
  )
}
